// src/components/NewsCarousel.jsx
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/newsCarousel.css";

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function NewsCarousel({
  items = [],
  itemsPerSlide = 4,
  onSlideChange,
  autoplay = true,
  autoplayInterval = 4000,
  pauseOnHover = true,
}) {
  const navigate = useNavigate();
  const slides = useMemo(
    () => chunk(items, itemsPerSlide),
    [items, itemsPerSlide],
  );
  const [current, setCurrent] = useState(0);

  const isPausedRef = useRef(false);
  const mountedRef = useRef(true);
  const pauseTimeoutRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
      clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  // inform parent
  useEffect(() => {
    if (onSlideChange) onSlideChange(current);
  }, [current, onSlideChange]);

  // keep current in-range if slides change
  useEffect(() => {
    if (current >= slides.length) {
      setCurrent(Math.max(0, slides.length - 1));
    }
  }, [slides.length]); // eslint-disable-line

  const play = useCallback(() => {
    if (!autoplay) return;
    // clear any existing interval first
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(
      () => {
        if (isPausedRef.current) return;
        setCurrent((s) => {
          const next = s === slides.length - 1 ? 0 : s + 1;
          return next;
        });
      },
      Math.max(1000, autoplayInterval),
    );
  }, [autoplay, autoplayInterval, slides.length]);

  // start autoplay when slides change / component mounts
  useEffect(() => {
    if (autoplay && slides.length > 1) {
      play();
    }
    return () => clearInterval(intervalRef.current);
  }, [play, slides.length, autoplay]);

  const prev = () => {
    setCurrent((s) => (s === 0 ? slides.length - 1 : s - 1));
    // temporarily pause autoplay to avoid immediate jump
    if (autoplay) {
      isPausedRef.current = true;
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = setTimeout(() => {
        isPausedRef.current = false;
      }, autoplayInterval);
    }
  };

  const next = () => {
    setCurrent((s) => (s === slides.length - 1 ? 0 : s + 1));
    if (autoplay) {
      isPausedRef.current = true;
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = setTimeout(() => {
        isPausedRef.current = false;
      }, autoplayInterval);
    }
  };

  const handleCardClick = (news, slideIndex) => {
    if (onSlideChange) onSlideChange(slideIndex);
    navigate(`/news/${news.id}`, { state: { news } });
  };

  // pause / resume handlers (for hover)
  const handleMouseEnter = () => {
    if (pauseOnHover) isPausedRef.current = true;
  };
  const handleMouseLeave = () => {
    if (pauseOnHover) isPausedRef.current = false;
  };

  if (!items || items.length === 0) return null;
  if (slides.length === 0) return null;

  return (
    <div
      className="news-carousel-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="news-carousel-header">
        <h3>Recent News</h3>
        <div className="news-carousel-controls">
          <button
            className="carousel-btn"
            onClick={prev}
            aria-label="Previous slide"
          >
            ◀
          </button>
          <button
            className="carousel-btn"
            onClick={next}
            aria-label="Next slide"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="news-carousel-view">
        <div
          className="news-carousel-track"
          style={{
            width: `${slides.length * 100}%`,
            transform: `translateX(-${(current * 100) / slides.length}%)`,
          }}
        >
          {slides.map((slideItems, slideIndex) => (
            <div
              className="news-carousel-slide"
              key={slideIndex}
              style={{ width: `${100 / slides.length}%` }}
            >
              {slideItems.map((news) => (
                <div
                  key={news.id}
                  className="news-card-carousel"
                  style={{ backgroundImage: `url(${news.img})` }}
                  onClick={() => handleCardClick(news, slideIndex)}
                >
                  <div className="news-card-text">
                    <h4 className="news-card-title">{news.title}</h4>
                    <div className="news-card-date">{news.date}</div>
                  </div>
                </div>
              ))}

              {Array.from({
                length: Math.max(0, itemsPerSlide - slideItems.length),
              }).map((_, i) => (
                <div key={`empty-${i}`} className="news-card-carousel empty" />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="news-carousel-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === current ? "active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
